import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import { wrapIndex } from "../lib/keyboard";

// 列尾的 ⋮ 選單。明細列上的次要動作(插入 / 排序 / 複製 / 刪除)全部收進這裡:
// 三顆按鈕一字排開會把 45 列的明細表變得很吵,而九成的時候一顆都不會按到。
//
// 收起來不等於藏起來,這幾條是硬性要求:
//
// 1. **鍵盤要走得完。** 拖曳排序對鍵盤與輔助科技使用者完全不可用,
//    所以「上移 / 下移」只能收進選單、不能被拖曳取代;選單本身也要
//    Enter/Space 開啟、上下鍵移動、Enter 執行、Esc 關閉。
// 2. **關閉後焦點回到 ⋮。** 否則按完一個動作,鍵盤使用者就被丟回頁首。
// 3. **portal 到 body + position: fixed。** 明細表外層是 overflow-x-auto,
//    CSS 規範說 overflow-x 非 visible 時 overflow-y 的 visible 會被算成 auto,
//    絕對定位的選單會被那個容器裁掉 —— 這是 InlineSuggest 已經踩過一次的坑。
// 4. **危險動作要標出來。** 刪除不是靠紅字暗示而已,還帶 data-danger,
//    程式與測試都認得。
// 5. **會讓 ⋮ 自己消失的動作,不能把焦點還給 ⋮。** 刪除會把整列連同觸發鈕
//    一起卸載,焦點於是掉回 <body>:鍵盤使用者下一次按 Tab 會從整份文件最上面
//    重來,完全失去位置感。那種項目請標成 destroysTrigger,由呼叫端接手焦點。

/** 選單寬度。項目都是四到五個中文字,固定寬度比自適應穩。 */
const MENU_WIDTH = 176;
/** 選單高度上限,用來判斷要往下開還是往上翻。 */
const MAX_HEIGHT = 288;

/**
 * 從 `from` 往 `delta` 的方向找下一個**按得動**的項目。
 *
 * 上下鍵停在按不動的東西上,鍵盤使用者會以為選單卡住了 —— 那一列仍然畫得出來、
 * 滑鼠滑過去仍然讀得到原因,只是巡覽跳過它。整組都按不動時回 -1,由呼叫端
 * 把焦點收在選單本身,Esc 與方向鍵才還收得到。
 */
function nextEnabled(items: RowMenuItem[], from: number, delta: number): number {
  if (items.length === 0) return -1;
  for (let step = 1; step <= items.length; step += 1) {
    const index = wrapIndex(from, items.length, delta * step);
    if (!items[index]?.disabled) return index;
  }
  return -1;
}

/** 由頭(或由尾)數過來第一個按得動的項目。開啟與 Home / End 都用它。 */
function firstEnabled(items: RowMenuItem[], fromEnd = false): number {
  const order = items.map((_, index) => (fromEnd ? items.length - 1 - index : index));
  return order.find((index) => !items[index]?.disabled) ?? -1;
}

export interface RowMenuItem {
  key: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** 危險動作(刪除)。標成 data-danger 並套 --danger 代幣。 */
  danger?: boolean;
  /**
   * 這個動作會讓觸發鈕自己消失(例如刪除整列)。
   *
   * 標了之後 RowMenu **不會**把焦點還給 ⋮ —— 那顆按鈕馬上就不存在了,還回去
   * 只會讓焦點掉到 <body>。焦點要交給誰由呼叫端決定(通常是相鄰列的 ⋮)。
   */
  destroysTrigger?: boolean;
  /** 這個動作目前做不得。**畫出來但按不動**,不要整個藏起來 ——
   *  藏起來只會讓人一直來問為什麼別人做得到自己做不到。 */
  disabled?: boolean;
  /** 為什麼做不得。掛在 title 上,滑過去就看得到。 */
  disabledReason?: string;
}

export interface RowMenuProps {
  items: RowMenuItem[];
  /**
   * 觸發鈕的無障礙名稱,例如「第 3 列 更多動作」。
   * ⋮ 本身沒有文字,少了這個名稱,螢幕閱讀器只會唸出「按鈕」。
   */
  label: string;
}

export function RowMenu({ items, label }: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = `nq-rowmenu-${useId()}`;

  /**
   * 依 ⋮ 的位置算出選單的固定座標,空間不足時往上翻。
   * 這一步要在 setOpen 之前跑完,選單才會在同一次 commit 裡帶著座標出現 ——
   * 否則第一次 render 沒有選單,移焦點的 effect 會撲空。
   */
  const place = useCallback(() => {
    const anchor = triggerRef.current;
    if (!anchor || typeof window === "undefined" || typeof anchor.getBoundingClientRect !== "function") return;
    const rect = anchor.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < MAX_HEIGHT && rect.top > spaceBelow;
    // 靠右對齊 ⋮,但不讓選單掉出視窗外。
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setStyle({
      position: "fixed",
      left,
      width: MENU_WIDTH,
      maxHeight: MAX_HEIGHT,
      ...(flipUp ? { bottom: Math.max(8, window.innerHeight - rect.top + 4) } : { top: rect.bottom + 4 }),
    });
  }, []);

  const openMenu = useCallback(() => {
    place();
    // 第一項按不動時不要停在它上面 —— 開起來就是一個按不下去的選單,
    // 鍵盤使用者還得自己多按一下才知道下面有東西。
    setActiveIndex(firstEnabled(items));
    setOpen(true);
  }, [place, items]);

  /** 關閉並把焦點還給 ⋮ —— 鍵盤使用者不能被丟回頁首。 */
  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    setActiveIndex(0);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  // 游標移到哪一項,真正的 DOM 焦點就跟到哪一項。
  // aria-activedescendant 在選單上的支援度不齊,直接移動焦點最可靠。
  //
  // 整組都按不動時焦點落在選單本身:disabled 的按鈕接不到焦點,不接手的話焦點會
  // 留在 ⋮ 上,而 Esc 與方向鍵是掛在選單上的 —— 使用者會關不掉自己打開的東西。
  useEffect(() => {
    if (!open) return;
    const target = activeIndex >= 0 ? itemRefs.current[activeIndex] : null;
    if (target && !target.disabled) {
      target.focus();
      return;
    }
    menuRef.current?.focus();
  }, [open, activeIndex]);

  // 開著的時候跟著捲動與縮放走。用捕獲階段才收得到表格容器自己的捲動。
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  // 點到選單與 ⋮ 以外的地方就收起來。這裡不還焦點:使用者是用滑鼠離開的。
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target)) return;
      if (document.getElementById(menuId)?.contains(target)) return;
      close(false);
    };
    document.addEventListener("mousedown", onPointerDown, true);
    return () => document.removeEventListener("mousedown", onPointerDown, true);
  }, [open, menuId, close]);

  const run = (item: RowMenuItem) => {
    // 按不動的項目連選單都不收:使用者按它是想知道為什麼,收起來只會讓他再開一次。
    if (item.disabled) return;
    // 會讓 ⋮ 自己消失的動作,不能把焦點還給它 —— 那顆按鈕在 onSelect() 之後
    // 就不在文件裡了,焦點會直接掉到 <body>。這種情況交給呼叫端接手。
    close(!item.destroysTrigger);
    item.onSelect();
  };

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      // 選單是浮在頁面上的,Tab 出去只會落在被它蓋住的東西上。兩個鍵都直接收起來。
      event.preventDefault();
      close();
      return;
    }
    // 四個巡覽鍵一律跳過按不動的項目(上下鍵不停在按不動的東西上),
    // 但那一列仍然畫得出來、仍然讀得到 disabledReason。
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => nextEnabled(items, prev < 0 ? items.length - 1 : prev, 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => nextEnabled(items, prev < 0 ? 0 : prev, -1));
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(firstEnabled(items));
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(firstEnabled(items, true));
    }
  };

  const menu =
    open && style !== null && typeof document !== "undefined"
      ? createPortal(
          <div
            id={menuId}
            ref={menuRef}
            role="menu"
            aria-label={label}
            style={style}
            // 整組項目都按不動時,焦點要落得住這裡,Esc 才關得掉。
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            className={
              "z-50 flex flex-col overflow-y-auto rounded-[var(--radius)] border " +
              "border-[var(--border-strong)] bg-[var(--surface)] p-1 shadow-card"
            }
          >
            {items.map((item, index) => {
              const button = (
                <button
                  key={item.key}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={index === activeIndex ? 0 : -1}
                  disabled={item.disabled}
                  // 原因掛在按鈕上,滑過去就看得到;名稱仍然只有 label ——
                  // title 只在元素沒有文字內容時才會被拿去當可及名稱。
                  title={item.disabled ? item.disabledReason : undefined}
                  data-danger={item.danger ? "true" : undefined}
                  onClick={() => run(item)}
                  onMouseEnter={() => (item.disabled ? undefined : setActiveIndex(index))}
                  className={
                    "flex w-full items-center gap-2 rounded-[var(--radius)] px-2 py-1.5 text-left text-sm " +
                    "disabled:cursor-not-allowed disabled:opacity-55 " +
                    (item.danger
                      ? "text-[var(--danger)] enabled:hover:bg-[var(--surface-2)] focus-visible:bg-[var(--surface-2)]"
                      : "text-[var(--text)] enabled:hover:bg-[var(--surface-2)] focus-visible:bg-[var(--surface-2)]")
                  }
                >
                  {item.icon ? <span className="shrink-0 text-[var(--text-3)]">{item.icon}</span> : null}
                  <span className="min-w-0 flex-1">{item.label}</span>
                </button>
              );
              if (!item.disabled) return button;
              // disabled 的按鈕在 Chrome / Safari 收不到滑鼠事件,自己的 title
              // 提示不會跳出來 —— 外面再包一層帶同一句話的容器,滑過去才真的讀得到。
              // role="none" 是為了不讓這層包裝破壞 menu → menuitem 的結構。
              return (
                <span key={item.key} role="none" title={item.disabledReason} className="flex w-full">
                  {button}
                </span>
              );
            })}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        // 呼叫端在刪除之後要靠這個屬性找到相鄰列的 ⋮ 來接手焦點
        data-row-menu-trigger="true"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close() : openMenu())}
        className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
      >
        <MoreVertical aria-hidden="true" size={16} />
      </button>
      {menu}
    </>
  );
}
